import React from 'react';

function formatDate(dateStr?: string) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function formatFileSize(bytes?: number) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getServiceModeLabel(mode?: string) {
  switch (mode) {
    case 'FULL_TIME':
      return '全职';
    case 'PART_TIME':
      return '兼职';
    case 'INTERNSHIP':
      return '实习';
    case 'PROJECT':
      return '外包/项目';
    default:
      return mode || '';
  }
}

export function getAppStatusInfo(status?: string) {
  const s = (status || '').toUpperCase();
  if (s === 'PENDING' || s === 'PENDING_REVIEW' || s === 'SUBMITTED') {
    return { label: '待审核', color: 'bg-amber-50 text-amber-700 border-amber-200/60', isPending: true };
  }
  if (s === 'ACCEPTED' || s === 'APPROVED' || s === 'PASSED') {
    return { label: '已包含/合适', color: 'bg-emerald-50 text-emerald-700 border-emerald-200/60', isPending: false };
  }
  if (s === 'REJECTED' || s === 'REFUSED') {
    return { label: '未通过/不合适', color: 'bg-red-50 text-red-600 border-red-200/60', isPending: false };
  }
  if (s === 'WITHDRAWN') {
    return { label: '已撤回', color: 'bg-slate-100 text-slate-500 border-slate-200/60', isPending: false };
  }
  return { label: status || '未知', color: 'bg-slate-100 text-slate-500 border-slate-200/60', isPending: false };
}

export function parseResumeSnapshot(app: Api.Resume.ApplicationResponse): any {
  if (!app) return null;
  const raw = (app as any).resume_snapshot || app.resume_snapshot_json;
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return null;
}

export interface JobApplicationCardProps {
  app: Api.Resume.ApplicationResponse;
  reviewingAppId?: string | null;
  onReview: (appId: string, accepted: boolean) => void;
  onViewSnapshot: (appId: string, appStatus: string, applicantName: string, snapshot: any) => void;
}

export default function JobApplicationCard({
  app,
  reviewingAppId,
  onReview,
  onViewSnapshot,
}: JobApplicationCardProps) {
  const statusInfo = getAppStatusInfo(app.status);
  const snapshot = parseResumeSnapshot(app);
  
  const applicantName = snapshot?.provider_profile?.display_name || snapshot?.name || '求职者';
  const phone = snapshot?.contact?.phone || snapshot?.phone;
  const email = snapshot?.contact?.email || snapshot?.email;
  const wechatId = snapshot?.contact?.wechat_id || snapshot?.wechat_id;
  const expectedSalary = snapshot?.expected_monthly_salary;
  const intro = snapshot?.provider_profile?.intro || snapshot?.self_introduction;
  const skillTags: string[] = snapshot?.provider_profile?.skill_tags || snapshot?.skill_tags || [];
  const serviceMode = getServiceModeLabel(snapshot?.provider_profile?.service_mode);
  const resumeFile = snapshot?.resume_file;

  return (
    <div className="p-4 rounded-xl border border-slate-200/80 bg-white hover:border-primary-200 hover:shadow-xs transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      {/* 申请人基本信息 */}
      <div className="space-y-1.5 flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-slate-900 text-sm">{applicantName}</span>
          {serviceMode && (
            <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-blue-50 text-blue-700 border border-blue-100">
              {serviceMode}
            </span>
          )}
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${statusInfo.color}`}>
            {statusInfo.label}
          </span>
          {expectedSalary && (
            <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md">
              期望月薪: ¥{expectedSalary}
            </span>
          )}
        </div>

        {/* 简介选读 */}
        {intro && (
          <p className="text-xs text-slate-600 line-clamp-1 bg-slate-50 p-1.5 rounded-lg border border-slate-100">
            {intro}
          </p>
        )}

        {/* 联系信息简报 */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
          {phone && (
            <span className="flex items-center gap-1">
              <i className="ri-phone-line text-slate-400"></i>
              {phone}
            </span>
          )}
          {email && (
            <span className="flex items-center gap-1">
              <i className="ri-mail-line text-slate-400"></i>
              {email}
            </span>
          )}
          {wechatId && (
            <span className="flex items-center gap-1">
              <i className="ri-wechat-line text-slate-400"></i>
              {wechatId}
            </span>
          )}
          <span className="flex items-center gap-1 text-slate-400">
            <i className="ri-time-line"></i>
            投递于 {formatDate(app.applied_at)}
          </span>
        </div>

        {/* 简历文件快捷指示 */}
        {resumeFile && (
          <div className="pt-0.5 flex items-center gap-2">
            <span className="text-slate-400 text-[11px]">简历附件：</span>
            {resumeFile.access_url ? (
              <a
                href={resumeFile.access_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 hover:underline font-medium"
              >
                <i className="ri-file-download-line"></i>
                {resumeFile.display_name || resumeFile.file_name || '简历文件'}
                {resumeFile.file_size ? ` (${formatFileSize(resumeFile.file_size)})` : ''}
              </a>
            ) : (
              <span className="text-xs text-slate-600">
                {resumeFile.display_name || resumeFile.file_name}
              </span>
            )}
          </div>
        )}

        {/* 技能标签 */}
        {skillTags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-0.5">
            {skillTags.slice(0, 5).map((tag, idx) => (
              <span
                key={idx}
                className="text-[11px] px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* 操作区 */}
      <div className="flex items-center gap-2 sm:flex-col sm:items-end flex-wrap flex-shrink-0">
        {snapshot && app.id && (
          <button
            type="button"
            onClick={() => onViewSnapshot(app.id!, app.status || '', applicantName, snapshot)}
            className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium transition-all cursor-pointer flex items-center gap-1"
          >
            <i className="ri-file-text-line"></i>
            查看简历快照
          </button>
        )}

        {statusInfo.isPending && (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={reviewingAppId === app.id}
              onClick={() => app.id && onReview(app.id, true)}
              className="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium transition-all cursor-pointer shadow-xs disabled:opacity-50 flex items-center gap-1"
            >
              {reviewingAppId === app.id ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                <i className="ri-check-line"></i>
              )}
              合适
            </button>
            <button
              type="button"
              disabled={reviewingAppId === app.id}
              onClick={() => app.id && onReview(app.id, false)}
              className="px-3 py-1.5 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 text-xs font-medium transition-all cursor-pointer disabled:opacity-50 flex items-center gap-1"
            >
              <i className="ri-close-line"></i>
              不合适
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
